import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api.js";

/* ------------------------------------------------------------------ types */

export interface UsageWindow { limitId: string; kind: "short" | "weekly" | "other"; durationMinutes: number; usedPercent: number | null; resetsAt: number | null; }
export type Provider = "claude" | "codex";
export const PROVIDERS: readonly Provider[] = ["claude", "codex"];

export interface DashboardProvider {
  provider: Provider; status: "ok" | "missing"; source: string; observedAt: string | null; stale: boolean; windows: UsageWindow[];
  tokenUsage: { status: "ok" | "missing"; todayTokens: number | null; totalTokens: number | null; todayCostUsd: number | null; totalCostUsd: number | null; costLabel: string; observedAt: string | null; stale: boolean };
}
export interface DashboardData { serverTime: string; providers: DashboardProvider[]; }
export interface AnalyticsPoint { start: number; tokens: number; costUsd: number; usedPercent: number | null; }
export interface ModelUsage { model: string; tokens: number; costUsd: number; }
export interface AnalyticsProvider { provider: Provider; points: AnalyticsPoint[]; models: ModelUsage[]; latest: { totalTokens: number; totalCostUsd: number; observedAt: number } | null; }
export interface AnalyticsData { from: number; to: number; bucket: string; providers: AnalyticsProvider[]; }
export interface TokenItem { provider: Provider; observedAt: number; todayTokens: number; totalTokens: number; todayCostUsd: number; totalCostUsd: number; }
export interface HistoryData { items: Array<{ provider: Provider; observedAt: number; windows: UsageWindow[] }>; tokenItems: TokenItem[]; }
export interface ConsoleUser { displayName: string; email: string; isAdmin: boolean; avatarVersion: string | null; avatarSrc?: string | null; }

/* ------------------------------------------------------------------ format */

export const LABEL: Record<Provider, string> = { claude: "Claude", codex: "Codex" };
export const COLOR: Record<Provider, string> = { claude: "#ff3b47", codex: "#2f7bff" };
export const DAY = 86_400;

export const compact = (value: number | null, digits = 2): string => {
  if (value === null) return "—";
  if (value >= 1e9) return `${(value / 1e9).toFixed(digits)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(digits)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
  return String(Math.round(value));
};
export const full = (value: number | null): string => value === null ? "—" : Math.round(value).toLocaleString("en-US");
export const money = (value: number | null): string => value === null ? "—" : `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const pct = (value: number | null): string => value === null ? "--" : `${Math.round(value)}%`;
export const remain = (resetsAt: number | null | undefined): string => {
  if (!resetsAt) return "--";
  const diff = resetsAt * 1000 - Date.now();
  if (diff <= 0) return "等待重置";
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  return days > 0 ? `${days}天${pad(hours)}小时` : `${hours}小时${pad(Math.floor((diff % 3_600_000) / 60_000))}分`;
};
export const pad = (value: number): string => String(value).padStart(2, "0");
/** MM/DD/YYYY — matches the design comps. */
export const dateText = (ms: number): string => { const d = new Date(ms); return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`; };
export const clockText = (ms: number): string => { const d = new Date(ms); return `${dateText(ms)} ${pad(d.getHours())}:${pad(d.getMinutes())}`; };
export const stampText = (value: string | number | null): string => {
  if (value === null) return "从未";
  const d = new Date(typeof value === "number" ? value * 1000 : value);
  return `${dateText(d.getTime())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
export const dayText = (seconds: number): string => { const d = new Date(seconds * 1000); return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}`; };
export const shortStamp = (seconds: number): string => { const d = new Date(seconds * 1000); return `${dayText(seconds)} ${pad(d.getHours())}:${pad(d.getMinutes())}`; };
export const rangeText = (days: number, data: { from: number; to: number } | null): string => {
  const to = data ? data.to * 1000 : Date.now();
  const from = data ? data.from * 1000 : to - (days - 1) * DAY * 1000;
  return `${dateText(from)} - ${dateText(to)}`;
};
export const win = (windows: UsageWindow[], minutes: number): UsageWindow | null => windows.find((item) => item.durationMinutes === minutes) ?? null;

/** Ring rule from the spec: show the 5h window; when it is empty fall back to the 7d window and say so. */
export const ringWindow = (windows: UsageWindow[]): { value: number | null; label: string } => {
  const five = win(windows, 300);
  if (five?.usedPercent !== null && five?.usedPercent !== undefined) return { value: five.usedPercent, label: "5小时用量" };
  const week = win(windows, 10080);
  if (week?.usedPercent !== null && week?.usedPercent !== undefined) return { value: week.usedPercent, label: "7天用量" };
  return { value: null, label: "5小时用量" };
};

/* ------------------------------------------------------------------ fixtures (localhost preview only) */

export const LOCAL = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
export const MOCK = LOCAL && typeof window !== "undefined" && new URLSearchParams(window.location.search).has("mock");
export const REFRESH_EVENT = "usagehub:refresh";

function mockDashboard(): DashboardData {
  const now = Math.floor(Date.now() / 1000);
  const iso = new Date().toISOString();
  return { serverTime: iso, providers: [
    { provider: "claude", status: "ok", source: "official_statusline", observedAt: iso, stale: false, windows: [{ limitId: "5h", kind: "short", durationMinutes: 300, usedPercent: 28, resetsAt: now + 3 * 3600 }, { limitId: "7d", kind: "weekly", durationMinutes: 10080, usedPercent: 24, resetsAt: now + 2 * DAY + 14 * 3600 + 120 }], tokenUsage: { status: "ok", todayTokens: 0, totalTokens: 17_340_000_000, todayCostUsd: 0, totalCostUsd: 14_396.95, costLabel: "标准 API 等价费用", observedAt: iso, stale: false } },
    { provider: "codex", status: "ok", source: "official_app_server", observedAt: iso, stale: false, windows: [{ limitId: "5h", kind: "short", durationMinutes: 300, usedPercent: null, resetsAt: null }, { limitId: "7d", kind: "weekly", durationMinutes: 10080, usedPercent: 24, resetsAt: now + 6 * DAY + 3 * 3600 + 120 }], tokenUsage: { status: "ok", todayTokens: 3_915_165, totalTokens: 26_610_000_000, todayCostUsd: 1.41, totalCostUsd: 16_718.04, costLabel: "标准 API 等价费用", observedAt: iso, stale: false } },
  ] };
}

const MOCK_MODELS: Record<Provider, ModelUsage[]> = {
  claude: [{ model: "claude-3.5-sonnet", tokens: 9_800_000_000, costUsd: 8_100.2 }, { model: "claude-3.5-haiku", tokens: 5_300_000_000, costUsd: 5_400.3 }, { model: "claude-3-opus", tokens: 1_700_000_000, costUsd: 800 }, { model: "claude-3-sonnet", tokens: 540_000_000, costUsd: 96.45 }],
  codex: [{ model: "gpt-4o-codex", tokens: 18_200_000_000, costUsd: 11_200 }, { model: "gpt-4.1", tokens: 7_400_000_000, costUsd: 5_300 }, { model: "o3-mini", tokens: 760_000_000, costUsd: 160 }, { model: "o1-preview", tokens: 250_000_000, costUsd: 58.04 }],
};

function mockAnalytics(days: number, toOverride?: number): AnalyticsData {
  const to = toOverride ?? Math.floor(Date.now() / 1000);
  const from = to - (days - 1) * DAY;
  const series = (seed: number, scale: number): AnalyticsPoint[] => Array.from({ length: days }, (_, index) => {
    const wave = Math.abs(Math.sin((index + seed) * 1.7)) * 0.62 + Math.abs(Math.cos((index + seed) * 0.6)) * 0.25 + 0.18;
    const value = wave * scale;
    return { start: from + index * DAY, tokens: Math.round(value), costUsd: Math.round(value / 3_600_000), usedPercent: null };
  });
  // Baseline requests (toOverride) model "older" cumulative snapshots so the per-model deltas look realistic.
  const old = toOverride !== undefined && to < Date.now() / 1000 - 2 * DAY;
  const shrink = (provider: Provider): number[] => toOverride === undefined ? [] : old ? [0.72, 0.86, 1, 1] : provider === "codex" ? [1 - 3_915_165 / 18_200_000_000] : [];
  const models = (provider: Provider): ModelUsage[] => MOCK_MODELS[provider].map((item, index) => ({ ...item, tokens: Math.round(item.tokens * (shrink(provider)[index] ?? 1)) }));
  return { from, to, bucket: "day", providers: [
    { provider: "claude", points: series(2, 4_600_000_000), models: models("claude"), latest: { totalTokens: 17_340_000_000, totalCostUsd: 14_396.95, observedAt: to } },
    { provider: "codex", points: series(5, 4_900_000_000), models: models("codex"), latest: { totalTokens: 26_610_000_000, totalCostUsd: 16_718.04, observedAt: to } },
  ] };
}

function mockHistory(provider: Provider): HistoryData {
  const now = Math.floor(Date.now() / 1000);
  const base = provider === "claude" ? { today: 0, total: 17_340_000_000, cost: 14_396.95 } : { today: 3_915_165, total: 26_610_000_000, cost: 16_718.04 };
  const tokenItems = Array.from({ length: 12 }, (_, index) => {
    const back = index * 11 * 60 + (index % 3) * 97;
    const today = Math.max(0, base.today - index * 312_401);
    return { provider, observedAt: now - back, todayTokens: today, totalTokens: base.total - index * 312_401, todayCostUsd: Math.max(0, Math.round((today / 2_780_000) * 100) / 100), totalCostUsd: base.cost - index * 0.11 };
  });
  return { items: [], tokenItems };
}

/* ------------------------------------------------------------------ hooks */

export function useDashboard(): { data: DashboardData | null; error: string; reload: () => void } {
  const [data, setData] = useState<DashboardData | null>(MOCK ? mockDashboard() : null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    if (MOCK) { setData(mockDashboard()); return; }
    void api<DashboardData>("/v1/dashboard").then((next) => { setData(next); setError(""); }).catch(() => setError("暂时读不到数据，检查采集器是否在运行。"));
  }, []);
  useEffect(() => { load(); const timer = window.setInterval(load, 120_000); return () => window.clearInterval(timer); }, [load]);
  return { data, error, reload: load };
}

export function useAnalytics(days: number): { data: AnalyticsData | null; error: string; reload: () => void } {
  const [data, setData] = useState<AnalyticsData | null>(MOCK ? mockAnalytics(days) : null);
  const [error, setError] = useState("");
  const requestVersion = useRef(0);
  const load = useCallback(() => {
    const version = ++requestVersion.current;
    if (MOCK) { setData(mockAnalytics(days)); setError(""); return; }
    const to = Math.floor(Date.now() / 1000);
    void api<AnalyticsData>(`/v1/analytics?from=${to - (days - 1) * 86_400}&to=${to}&bucket=day`)
      .then((next) => { if (version === requestVersion.current) { setData(next); setError(""); } })
      .catch(() => { if (version === requestVersion.current) setError("统计数据读取失败。"); });
  }, [days]);
  useEffect(() => {
    load();
    window.addEventListener(REFRESH_EVENT, load);
    return () => { requestVersion.current += 1; window.removeEventListener(REFRESH_EVENT, load); };
  }, [load]);
  return { data, error, reload: load };
}

/**
 * Per-model totals are cumulative (newest snapshot wins), so a window's usage is "now minus the newest snapshot
 * at the window boundary". This reads that boundary snapshot through the same analytics endpoint. Returns null
 * per provider when no snapshot sits close enough to the boundary to make the subtraction honest.
 */
export function useModelBaseline(boundary: number, tolerance: number): Record<Provider, Map<string, number> | null> {
  const empty = { claude: null, codex: null };
  const [data, setData] = useState<Record<Provider, Map<string, number> | null>>(empty);
  const requestVersion = useRef(0);
  const load = useCallback(() => {
    const version = ++requestVersion.current;
    const apply = (next: AnalyticsData): void => {
      if (version !== requestVersion.current) return;
      const pick = (provider: Provider): Map<string, number> | null => {
        const item = next.providers.find((entry) => entry.provider === provider);
        if (!item?.latest || boundary - item.latest.observedAt > tolerance || !item.models.length) return null;
        return new Map(item.models.map((model) => [model.model, model.tokens]));
      };
      setData({ claude: pick("claude"), codex: pick("codex") });
    };
    if (MOCK) { apply(mockAnalytics(7, boundary)); return; }
    void api<AnalyticsData>(`/v1/analytics?from=${boundary - 30 * DAY}&to=${boundary}&bucket=day`).then(apply).catch(() => { if (version === requestVersion.current) setData(empty); });
  }, [boundary, tolerance]);
  useEffect(() => {
    load();
    window.addEventListener(REFRESH_EVENT, load);
    return () => { requestVersion.current += 1; window.removeEventListener(REFRESH_EVENT, load); };
  }, [load]);
  return data;
}

export function useHistory(provider: Provider, days: number, limit: number): HistoryData | null {
  const [history, setHistory] = useState<HistoryData | null>(MOCK ? mockHistory(provider) : null);
  useEffect(() => {
    let active = true;
    const load = (): void => {
      if (MOCK) { setHistory(mockHistory(provider)); return; }
      const to = Math.floor(Date.now() / 1000);
      void api<HistoryData>(`/v1/history?provider=${provider}&from=${to - (days - 1) * 86_400}&to=${to}&limit=${limit}`)
        .then((next) => { if (active) setHistory(next); })
        .catch(() => { if (active) setHistory(null); });
    };
    load();
    window.addEventListener(REFRESH_EVENT, load);
    return () => { active = false; window.removeEventListener(REFRESH_EVENT, load); };
  }, [provider, days, limit]);
  return history;
}
